import { EdgeLabelRenderer, getSmoothStepPath, type Position } from '@xyflow/react';
import { useEffect, useState } from 'react';
import { css } from 'aphrodite';
import { type FlowContact } from '../types';
import { contactFlowVisualizerStyles as styles } from './styles/ContactFlowVisualizerStyles';
import { brandPrimary } from '../../../styles/colors';
import { getPrincipalInitials } from '../../../../models/NameUtils';

/**
 * Color Mapping:
 * #3b82f6 → brandPrimary (fallback color for contacts without color)
 */

interface ContactFlowVisualizerProps {
	edgeId: string;
	sourceX: number;
	sourceY: number;
	targetX: number;
	targetY: number;
	sourcePosition: Position;
	targetPosition: Position;
	activeContacts: FlowContact[];
}

export function ContactFlowVisualizer({
	edgeId,
	sourceX,
	sourceY,
	targetX,
	targetY,
	sourcePosition,
	targetPosition,
	activeContacts,
}: ContactFlowVisualizerProps) {
	const [_animationProgress, _setAnimationProgress] = useState(0);

	useEffect(() => {
		// Animation is now handled by SVG animateMotion, but we keep this for future enhancements
		if (activeContacts.length === 0) {
			return;
		}
	}, [activeContacts.length]);

	if (activeContacts.length === 0) {
		return null;
	}

	const [edgePath] = getSmoothStepPath({
		sourceX,
		sourceY,
		sourcePosition,
		targetX,
		targetY,
		targetPosition,
	});

	const deltaX = targetX - sourceX;
	const deltaY = targetY - sourceY;
	const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
	const duration = Math.max(4, distance / 80);

	const pathId = `edge-path-${edgeId.replace(/[^a-zA-Z0-9]/g, '-')}`;

	return (
		<EdgeLabelRenderer>
			<svg className={css(styles.svgContainer)}>
				<defs>
					<path id={pathId} d={edgePath} fill='none' />
				</defs>

				{activeContacts.map((contact, index) => {
					const initials = getPrincipalInitials(contact);
					// Stagger the animation start time for multiple contacts (30% offset)
					const staggerDelay = (index * 0.3 * duration).toFixed(2);

					return (
						<g key={`${contact.id}-${edgeId}`}>
							<foreignObject width='36' height='36' x='-18' y='-18' className={css(styles.foreignObject)}>
								<div
									className={css(styles.contactAvatar)}
									style={{
										backgroundColor: contact.color || brandPrimary,
									}}
									title={`${contact.firstName} ${contact.lastName}`}
								>
									<span className={css(styles.contactInitials)}>{initials}</span>
								</div>
							</foreignObject>
							<animateMotion dur={`${duration}s`} repeatCount='indefinite' begin={`${staggerDelay}s`}>
								<mpath href={`#${pathId}`} />
							</animateMotion>
						</g>
					);
				})}
			</svg>
		</EdgeLabelRenderer>
	);
}
